import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_REDIRECT_DELAY_MS, AuthErrorFallback } from '@components/auth-error-fallback';
import { AuthLoadingSkeleton } from '@components/auth-loading-skeleton';
import { REDIRECT_URL, SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/feature-flag/feature-flag-context';
import { UserContext } from '@contexts/user/user-context';
import { useWebviewEvents } from '@contexts/webview-bridge';
import { useChangeUser } from '@hooks/auth/use-change-user';
import { useSignin } from '@hooks/auth/use-signin';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useGetUser } from '@hooks/user/user-get-user';
import { usePostHog } from '@posthog/react';
import { syncImpersonationCapture } from '@posthog-app/impersonation-capture';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { isImpersonating } from '@utils/auth/is-impersonating';
import { persistImpersonatorUserId } from '@utils/auth/persist-impersonator-user-id';
import { readImpersonatorUserId } from '@utils/auth/read-impersonator-user-id';
import { removeImpersonatorUserId } from '@utils/auth/remove-impersonator-user-id';
import { setCurrencyPreferences } from '@utils/price-formatter/set-currency-preferences';
import { getTokenUserId } from '@utils/token/get-token-user-id';

import { AuthContext } from './auth-context';

// Dev auto-login credentials (only set in .env.dev for localhost development)
const DEV_EMAIL = import.meta.env.VITE_EMAIL as string | undefined;
const DEV_PASSWORD = import.meta.env.VITE_PASSWORD as string | undefined;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { getTokens, setTokens, clearTokens } = useTokenManagement();
  const { sendLogout } = useWebviewEvents();
  const posthog = usePostHog();

  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const { setUserState, setIsUserLoading } = useContext(UserContext);
  // hideError: auto-login failures are handled gracefully (fallback to signOut + redirect),
  // so we suppress the default error toast to avoid noisy UX on stale dev credentials.
  const { mutateAsync: signinAsync } = useSignin({ hideError: true });
  const autoLoginAttemptedRef = useRef(false);

  // Get current tokens to determine authentication state
  const tokens = getTokens();

  // User is authenticated if they have a valid access token
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!tokens.accessToken;
  });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectUrlRef = useRef('');
  const isErrorRedirectRef = useRef(false);
  const [impersonateUser, setImpersonateUser] = useState<{ email: string; uuid: string }>({ email: '', uuid: '' });

  const { data: impersonateCredentials, isError: isImpersonateError } = useChangeUser({
    uuid: impersonateUser.uuid,
    options: { enabled: !!impersonateUser.uuid && !isPublicEventReportPage },
  });

  // Enable user query if we have any token (access or refresh)
  // The refresh flow will handle token renewal automatically
  const hasAnyToken = !!(tokens.accessToken ?? tokens.refreshToken);

  const {
    data: user,
    isLoading: isUserLoading,
    isError,
  } = useGetUser({
    enabled: hasAnyToken && !isPublicEventReportPage,
    retry: 2,
  });

  const isImpersonated = isImpersonating({
    impersonatorUserId: readImpersonatorUserId(),
    parked: getTokens({ isImpersonate: true }),
    current: tokens,
  });

  const signOut = useCallback(() => {
    const currentUrl = globalThis.location.href;
    // Fire-and-forget bridge notification so the xceedPro Android shell can
    // clear its own session in parallel. `isErrorRedirectRef.current` is set
    // by error-path callers (attemptAutoLoginOrSignOut, recoverSessionOrSignOut)
    // before signOut runs, so it correctly distinguishes the two flows.
    sendLogout({ reason: isErrorRedirectRef.current ? 'Auth error' : 'User initiated' }).catch((): void => undefined);
    posthog?.reset();
    clearTokens();
    removeImpersonatorUserId();
    queryClient.clear();
    redirectUrlRef.current = `${REDIRECT_URL}=${currentUrl}`;
    setIsRedirecting(true);
  }, [clearTokens, posthog, queryClient, sendLogout]);

  /**
   * Attempt dev auto-login using .env credentials.
   * Only runs once per session (guarded by autoLoginAttemptedRef).
   * On success: stores tokens and reloads the page.
   * On failure: falls back to signOut.
   */
  const attemptAutoLoginOrSignOut = useCallback(
    async ({ isErrorFlow }: { isErrorFlow: boolean }) => {
      const fallbackSignOut = () => {
        if (isErrorFlow) isErrorRedirectRef.current = true;
        signOut();
      };

      // If an auto-login attempt is already in flight or has finished, do nothing
      // so we don't cancel the pending signin.
      if (autoLoginAttemptedRef.current) return;

      // No dev credentials: fall back to the normal signOut + redirect flow.
      if (!DEV_EMAIL || !DEV_PASSWORD) {
        fallbackSignOut();
        return;
      }

      autoLoginAttemptedRef.current = true;
      try {
        const authResponse = await signinAsync({ email: DEV_EMAIL, password: DEV_PASSWORD });
        setTokens({ accessToken: authResponse.accessToken, refreshToken: authResponse.refreshToken });
        globalThis.location.reload();
      } catch {
        fallbackSignOut();
      }
    },
    [signinAsync, setTokens, signOut]
  );

  const recoverSessionOrSignOut = useCallback(
    () => attemptAutoLoginOrSignOut({ isErrorFlow: true }),
    [attemptAutoLoginOrSignOut]
  );

  const changeUser = useCallback(
    ({ email, uuid }: { email: string; uuid: string }) => {
      const { accessToken, refreshToken } = getTokens();
      if (accessToken && refreshToken) {
        setTokens({ accessToken, refreshToken, isImpersonate: true });
        // Store the admin's id now, while the normal keys are still the admin's, so the check
        // survives a restart that leaves the parked access token undecodable.
        const impersonatorUserId = getTokenUserId(accessToken);
        if (impersonatorUserId) persistImpersonatorUserId(impersonatorUserId);
        setImpersonateUser({ email, uuid });
      }
    },
    [getTokens, setTokens]
  );

  const resetAdminUser = useCallback(() => {
    const { refreshToken: adminRefreshToken, accessToken: adminAccessToken } = getTokens({ isImpersonate: true });
    if (adminAccessToken || adminRefreshToken) {
      setTokens({ accessToken: adminAccessToken ?? undefined, refreshToken: adminRefreshToken ?? '' });
    }
    clearTokens({ isImpersonate: true });
    removeImpersonatorUserId();
    // The impersonated identity must go before capture resumes on the next load, otherwise
    // the admin's own events would land on the customer's person.
    posthog?.reset();
    globalThis.location.reload();
  }, [getTokens, setTokens, clearTokens, posthog]);

  useEffect(() => {
    if (impersonateCredentials) {
      setTokens({
        accessToken: impersonateCredentials.accessToken,
        refreshToken: impersonateCredentials.refreshToken,
      });
      globalThis.open(SELECT_VENUE_ROUTE, '_self');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateCredentials]);

  // A signin that never answered leaves the admin's tokens parked with nothing to come back from,
  // which reads as a permanent impersonation of themselves. `changeUser` parks before it asks, so
  // only the caller can undo it.
  useEffect(() => {
    if (!isImpersonateError) return;
    clearTokens({ isImpersonate: true });
    removeImpersonatorUserId();
    setImpersonateUser({ email: '', uuid: '' });
  }, [isImpersonateError, clearTokens]);

  // Sweeps the leftovers of an impersonation that ended somewhere the app never saw: the tokens
  // outlive it, so without this they keep every consumer of `isImpersonated` in the old state for
  // good. Nothing notifies the app that it ended, so this is not event-driven — it heals on the next
  // render that recomputes `isImpersonated` (a navigation, a query settling, a dep below changing),
  // not the instant it ends. Held off while a `changeUser` is in flight — the parked tokens are the
  // admin's own and the impersonated ones have not landed yet, so both sides read as the same person
  // for that window and sweeping there would burn the return trip.
  useEffect(() => {
    if (isImpersonated || impersonateUser.uuid) return;
    const { accessToken, refreshToken } = getTokens({ isImpersonate: true });
    if (!accessToken && !refreshToken && !readImpersonatorUserId()) return;
    clearTokens({ isImpersonate: true });
    removeImpersonatorUserId();
  }, [isImpersonated, impersonateUser.uuid, getTokens, clearTokens]);

  // Declared before the identify effect below: within a commit, effects run in declaration order,
  // so capture opt-out settles before identify() switches the distinct_id to the impersonated user
  // on the load that lands as that user.
  useEffect(() => {
    if (!posthog) return;
    syncImpersonationCapture(posthog, { isImpersonated });
  }, [posthog, isImpersonated]);

  useEffect(() => {
    if (isPublicEventReportPage) setIsUserLoading(false);
    else setIsUserLoading(isUserLoading);
    if (user) {
      Sentry.setUser({ email: user.email, id: user.id });
      // Impersonated sessions identify as the impersonated user so feature flags resolve as
      // that user sees them; capture is off for the duration (syncImpersonationCapture), so
      // this pollutes nothing. posthog persists identification across sessions; re-identifying
      // the same user on every page load triggers a redundant feature-flag reload, so only
      // identify on an actual change.
      if (posthog && posthog.get_distinct_id() !== user.id) {
        posthog.identify(user.id, { email: user.email });
      }
      setUserState(user);
      i18n.changeLanguage(user.lang);
      setCurrencyPreferences(user.currencyFormat, user.currencyDisplay, user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, i18n, isUserLoading, isPublicEventReportPage]);

  useEffect(() => {
    if (isPublicEventReportPage) {
      setIsAuthenticated(true);
      return;
    }

    // Only sign out if there's no refresh token (can't recover)
    const currentTokens = getTokens();
    if (!currentTokens.refreshToken) {
      attemptAutoLoginOrSignOut({ isErrorFlow: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicEventReportPage]);

  // If user query fails after all retries, attempt dev auto-login or sign out
  useEffect(() => {
    if (!isError || isPublicEventReportPage) return;
    attemptAutoLoginOrSignOut({ isErrorFlow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPublicEventReportPage]);

  // Redirect to login when signing out
  // Error redirects wait AUTH_REDIRECT_DELAY_MS so the user sees the error page;
  // voluntary logouts redirect immediately.
  useEffect(() => {
    if (!isRedirecting) return;

    const loginUrl = redirectUrlRef.current || `${REDIRECT_URL}=${globalThis.location.href}`;
    const delay = isErrorRedirectRef.current ? AUTH_REDIRECT_DELAY_MS : 0;
    const timer = setTimeout(() => {
      globalThis.location.href = loginUrl;
    }, delay);

    return () => clearTimeout(timer);
  }, [isRedirecting]);

  // Repo rule, and Sonar S6481: a fresh object here re-renders every consumer on every
  // render of this provider, which wraps the whole app. Pre-existing, and fixed in this
  // PR because the split made it cheap to verify — touching this file now selects one
  // test file instead of 222.
  const value = useMemo(
    () => ({ isAuthenticated, signOut, recoverSessionOrSignOut, changeUser, resetAdminUser, isImpersonated }),
    [isAuthenticated, signOut, recoverSessionOrSignOut, changeUser, resetAdminUser, isImpersonated]
  );

  return (
    <AuthContext.Provider value={value}>
      {isRedirecting ? (
        <AuthLoadingSkeleton>{isErrorRedirectRef.current ? <AuthErrorFallback /> : null}</AuthLoadingSkeleton>
      ) : isPublicEventReportPage || user ? (
        children
      ) : (
        <AuthLoadingSkeleton />
      )}
    </AuthContext.Provider>
  );
};
